import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import { IClientControlOption } from '@/Interfaces/ControlOption.interface';
import AutocompleteControl from '@/components/Form/AutocompleteControl';
import { getFieldErrorProps } from '@/helpers/form';
import { useAppSelector } from '@/store/hooks';
import { selectAdvertisersCategories } from '@/store/selectors/advertisers.selectors';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { selectRibeyeAllClients } from '@/store/selectors/sharedApi.selectors';
import { DOMAIN_HINT, DOMAIN_RULES } from '@/utils/validationRules';
import { InputLabel, TextField } from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

const AdvertiserInfo = ({ isCreate = false }: { isCreate?: boolean }) => {
  const { control } = useFormContext();

  const clients = useAppSelector(selectRibeyeAllClients);
  const categories = useAppSelector(selectAdvertisersCategories);
  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.ADVERTISERS));

  const selectedClient: IClientControlOption | null = useWatch({ control, name: 'client' });

  const agencies = useMemo(() => selectedClient?.agencies || [], [selectedClient]);

  return (
    <>
      <div>
        <InputLabel htmlFor='name'>
          Name<span className='text-[#fe4033]'>*</span>
        </InputLabel>
        <Controller
          name='name'
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Please enter name',
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              placeholder='Enter Advertiser Name'
              id='name'
              disabled={readOnly}
              {...getFieldErrorProps(fieldState)}
            />
          )}
        />
      </div>
      <div className='mt-4'>
        <InputLabel htmlFor='client'>
          Client<span className='text-[#fe4033]'>*</span>
        </InputLabel>
        <AutocompleteControl
          required
          control={control}
          options={clients}
          name='client'
          id='client-select'
          placeholder='Select Client'
          disabled={readOnly}
        />
      </div>
      <div className='mt-4'>
        <InputLabel htmlFor='agency'>Agency</InputLabel>
        <AutocompleteControl
          control={control}
          disabled={!selectedClient || readOnly}
          options={agencies}
          name='agency'
          id='agency-select'
          placeholder='Select Agency'
        />
      </div>
      <div className='mt-4'>
        <InputLabel htmlFor='domain'>
          Domain<span className='text-[#fe4033]'>*</span>
        </InputLabel>
        <Controller
          name='domain'
          control={control}
          rules={DOMAIN_RULES}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              disabled={readOnly}
              placeholder='https://'
              id='domain'
              helperText={fieldState.error?.message || DOMAIN_HINT}
              {...getFieldErrorProps(fieldState)}
            />
          )}
        />
      </div>
      <div className='mt-4 flex gap-[15px]'>
        <div className='flex-1'>
          <InputLabel htmlFor='category'>
            Advertiser Category<span className='text-[#fe4033]'>*</span>
          </InputLabel>
          <AutocompleteControl
            disabled={readOnly}
            required
            control={control}
            options={categories}
            name='category'
            id='category-select'
            placeholder='Select Category'
          />
        </div>
        {!isCreate && (
          <div className='flex-1'>
            <InputLabel htmlFor='alternativeId'>Alternative ID</InputLabel>
            <Controller
              name='alternativeId'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  placeholder='Enter ID'
                  id='alternativeId'
                  disabled
                />
              )}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AdvertiserInfo;
